/**
 * エグゼクティブサマリーカード (shadcn/ui移行完了 + 実データ統合)
 * @description 売上の概要と即座に取るべきアクションを表示
 */

import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  TrendingUp,
  TrendingDown,
  Target,
  CalendarDays,
  CalendarRange,
  Info
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useDashboardData } from '@/hooks/useDashboardData';

interface ExecutiveSummaryProps {
  /** 本日の売上目標 */
  targetRevenue?: number;
  /** 昨日の売上 */
  yesterdayRevenue?: number;
  /** 前年同期比成長率 */
  yoyGrowth?: number;
  /** 今月の売上目標 */
  monthlyTarget?: number;
  /** 月次前年同期比成長率 */
  monthlyYoyGrowth?: number;
}

/**
 * エグゼクティブサマリーコンポーネント（実データ統合版）
 */
export const ExecutiveSummary: React.FC<ExecutiveSummaryProps> = ({
  targetRevenue = 150000,
  yoyGrowth = 12.5,
  monthlyTarget = 3500000,
  monthlyYoyGrowth = 18.2
}) => {
  // 実データ取得
  const { metrics, loading, error } = useDashboardData();

  // 実データから売上値を取得
  const todayRevenue = metrics.todayRevenue;
  const monthlyRevenue = metrics.monthlySales;

  // 本日の計算値
  const achievementRate = Math.min((todayRevenue / targetRevenue) * 100, 120);
  const forecastEndOfDay = Math.floor(todayRevenue * 1.15);
  const isAboveTarget = achievementRate >= 100;

  // 月次の計算値
  const monthlyAchievementRate = Math.min((monthlyRevenue / monthlyTarget) * 100, 120);
  const daysInMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
  const currentDay = new Date().getDate();
  const forecastEndOfMonth = Math.floor((monthlyRevenue / currentDay) * daysInMonth);
  const isAboveMonthlyTarget = monthlyAchievementRate >= 100;

  // 前月比計算（暫定: 前月売上を実売上の95%として計算 - 後で実データ対応）
  const estimatedLastMonthSales = monthlyRevenue * 0.95;
  const monthlyGrowthVsLast = estimatedLastMonthSales > 0
    ? ((monthlyRevenue - estimatedLastMonthSales) / estimatedLastMonthSales) * 100
    : 0;

  // プログレスバーの色クラス
  const getProgressColorClass = (rate: number): string => {
    if (rate >= 90) return 'bg-green-600';
    if (rate >= 70) return 'bg-yellow-600';
    return 'bg-red-600';
  };

  // Loading状態
  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card className="border shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center gap-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <p className="text-sm text-muted-foreground">データを読み込み中...</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  // Error状態
  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Alert variant="destructive">
          <AlertDescription>
            データの読み込みに失敗しました: {error}
          </AlertDescription>
        </Alert>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card className="border shadow-md hover:-translate-y-0.5 transition-all">
        <CardContent className="p-6">
          {/* ヘッダー */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold text-primary">
                本日の売上概要
              </h2>
            </div>
            <Badge variant="outline" className="border-green-600 text-green-600">
              リアルタイム更新
            </Badge>
          </div>

          {/* メイン売上表示 */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-6">
            {/* 本日の売上 */}
            <div className="md:col-span-6">
              <div className="flex items-center gap-2 mb-2">
                <p className="text-sm text-muted-foreground">
                  本日の売上
                </p>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[200px]">
                      <p className="text-xs">本日完了した予約の売上合計です</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-3xl font-bold text-primary">
                  ¥{todayRevenue.toLocaleString()}
                </span>
                <div className="flex items-center gap-1">
                  {yoyGrowth !== undefined ? (
                    <>
                      {yoyGrowth > 0 ? (
                        <TrendingUp className="h-4 w-4 text-green-600" />
                      ) : (
                        <TrendingDown className="h-4 w-4 text-red-600" />
                      )}
                      <span className={`text-sm font-medium ${yoyGrowth > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        前年同期比 {yoyGrowth > 0 ? '+' : ''}{yoyGrowth.toFixed(1)}%
                      </span>
                    </>
                  ) : (
                    <span className="text-sm font-medium text-muted-foreground">
                      前年同期比 N/A
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* 目標達成率 */}
            <div className="md:col-span-3">
              <div className="flex items-center gap-2 mb-2">
                <p className="text-sm text-muted-foreground">
                  目標達成率
                </p>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[200px]">
                      <p className="text-xs">本日の目標¥{targetRevenue.toLocaleString()}に対する達成率です</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-2xl font-bold text-primary">
                  {achievementRate.toFixed(1)}%
                </span>
                <Target className={`h-5 w-5 ${isAboveTarget ? 'text-green-600' : 'text-yellow-600'}`} />
              </div>
              <Progress
                value={Math.min(achievementRate, 100)}
                className={`h-2 [&>div]:${getProgressColorClass(achievementRate)}`}
              />
              <p className="text-xs text-muted-foreground mt-1">
                目標: ¥{targetRevenue.toLocaleString()}
              </p>
            </div>

            {/* 終日予測 */}
            <div className="md:col-span-3">
              <div className="flex items-center gap-2 mb-2">
                <p className="text-sm text-muted-foreground">
                  終日予測
                </p>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[200px]">
                      <p className="text-xs">現在のペースで推移した場合の終日予測売上です</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <span className="text-2xl font-bold text-primary block mb-1">
                ¥{forecastEndOfDay.toLocaleString()}
              </span>
              <p className="text-xs text-muted-foreground">
                現在のペースで推移した場合
              </p>
            </div>
          </div>

          <Separator className="my-6" />

          {/* 月次売上表示 */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-4">
              <CalendarRange className="h-5 w-5 text-primary" />
              <h3 className="text-lg font-semibold text-primary">
                今月の売上概要
              </h3>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* 今月の累計売上 */}
            <div className="md:col-span-6">
              <div className="flex items-center gap-2 mb-2">
                <p className="text-sm text-muted-foreground">
                  今月の累計売上
                </p>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[200px]">
                      <p className="text-xs">今月（1日〜今日）の売上合計です</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-3xl font-bold text-secondary-foreground">
                  ¥{monthlyRevenue.toLocaleString()}
                </span>
                <div className="flex items-center gap-1">
                  {monthlyYoyGrowth !== undefined ? (
                    <>
                      {monthlyYoyGrowth > 0 ? (
                        <TrendingUp className="h-4 w-4 text-green-600" />
                      ) : (
                        <TrendingDown className="h-4 w-4 text-red-600" />
                      )}
                      <span className={`text-sm font-medium ${monthlyYoyGrowth > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        前年同月比 {monthlyYoyGrowth > 0 ? '+' : ''}{monthlyYoyGrowth.toFixed(1)}%
                      </span>
                    </>
                  ) : (
                    <span className="text-sm font-medium text-muted-foreground">
                      前年同月比 N/A
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 mt-2">
                {monthlyGrowthVsLast > 0 ? (
                  <TrendingUp className="h-3 w-3 text-blue-600" />
                ) : (
                  <TrendingDown className="h-3 w-3 text-yellow-600" />
                )}
                <span className={`text-xs ${monthlyGrowthVsLast > 0 ? 'text-blue-600' : 'text-yellow-600'}`}>
                  前月比 {monthlyGrowthVsLast > 0 ? '+' : ''}{monthlyGrowthVsLast.toFixed(1)}%
                </span>
              </div>
            </div>

            {/* 月次目標達成率 */}
            <div className="md:col-span-3">
              <div className="flex items-center gap-2 mb-2">
                <p className="text-sm text-muted-foreground">
                  月次目標達成率
                </p>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[200px]">
                      <p className="text-xs">月間目標¥{monthlyTarget.toLocaleString()}に対する達成率です</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-2xl font-bold text-secondary-foreground">
                  {monthlyAchievementRate.toFixed(1)}%
                </span>
                <Target className={`h-5 w-5 ${isAboveMonthlyTarget ? 'text-green-600' : 'text-yellow-600'}`} />
              </div>
              <Progress
                value={Math.min(monthlyAchievementRate, 100)}
                className={`h-2 [&>div]:${getProgressColorClass(monthlyAchievementRate)}`}
              />
              <p className="text-xs text-muted-foreground mt-1">
                目標: ¥{monthlyTarget.toLocaleString()}
              </p>
            </div>

            {/* 月末予測 */}
            <div className="md:col-span-3">
              <div className="flex items-center gap-2 mb-2">
                <p className="text-sm text-muted-foreground">
                  月末予測
                </p>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[200px]">
                      <p className="text-xs">現在のペースで月末まで推移した場合の予測売上です</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <span className="text-2xl font-bold text-secondary-foreground block mb-1">
                ¥{forecastEndOfMonth.toLocaleString()}
              </span>
              <p className="text-xs text-muted-foreground mb-2">
                現在のペースで推移した場合
              </p>
              <p className={`text-xs font-medium ${forecastEndOfMonth >= monthlyTarget ? 'text-green-600' : 'text-yellow-600'}`}>
                目標との差: {forecastEndOfMonth >= monthlyTarget ? '+' : ''}¥{(forecastEndOfMonth - monthlyTarget).toLocaleString()}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};
